<script setup>
import { ref, defineEmits } from 'vue'
import axios from 'axios'

const emit = defineEmits(['close', 'table-created'])

const tableName = ref('')
const columns = ref([
  {
    name: 'id',
    type: 'int',
    length: 11,
    nullable: false,
    primary: true,
    autoIncrement: true,
    default: ''
  }
])

const columnTypes = [
  'varchar', 'text', 'longtext', 'int', 'bigint', 'decimal', 'float', 'double',
  'boolean', 'tinyint', 'date', 'datetime', 'timestamp', 'time', 'json'
]

const addColumn = () => {
  columns.value.push({
    name: '',
    type: 'varchar',
    length: 255,
    nullable: true,
    primary: false,
    autoIncrement: false,
    default: ''
  })
}

const removeColumn = (index) => {
  if (columns.value.length > 1) {
    columns.value.splice(index, 1)
  }
}

const createTable = async () => {
  if (!tableName.value.trim()) {
    alert('Please enter a table name')
    return
  }

  if (columns.value.some(col => !col.name.trim())) {
    alert('Please fill in all column names')
    return
  }

  try {
    await axios.post('/api/database-tables', {
      name: tableName.value,
      columns: columns.value
    })
    
    emit('table-created')
  } catch (error) {
    console.error('Error creating table:', error)
    alert('Error creating table: ' + (error.response?.data?.message || error.message))
  }
}

const close = () => {
  emit('close')
}
</script>

<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1001]">
    <div class="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
      <!-- Header -->
      <div class="bg-gray-50 px-6 py-4 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <h3 class="text-lg font-semibold text-gray-900">Create New Table</h3>
          <button @click="close" class="text-gray-400 hover:text-gray-600">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="p-6 overflow-y-auto" style="max-height: calc(90vh - 140px)">
        <!-- Table Name -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2">Table Name</label>
          <input
            v-model="tableName"
            type="text"
            class="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="my_table"
          >
        </div>

        <!-- Columns -->
        <div class="mb-6">
          <div class="flex items-center justify-between mb-4">
            <h4 class="text-md font-medium text-gray-900">Columns</h4>
            <button
              @click="addColumn"
              class="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg text-sm flex items-center"
            >
              <i class="fas fa-plus mr-2"></i>
              Add Column
            </button>
          </div>

          <div class="space-y-4">
            <div
              v-for="(column, index) in columns"
              :key="index"
              class="bg-gray-50 p-4 rounded-lg border border-gray-200"
            >
              <div class="grid grid-cols-12 gap-4 items-start">
                <!-- Column Name -->
                <div class="col-span-3">
                  <label class="block text-xs font-medium text-gray-700 mb-1">Name</label>
                  <input
                    v-model="column.name"
                    type="text"
                    class="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                    placeholder="column_name"
                  >
                </div>

                <!-- Type -->
                <div class="col-span-2">
                  <label class="block text-xs font-medium text-gray-700 mb-1">Type</label>
                  <select
                    v-model="column.type"
                    class="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option v-for="type in columnTypes" :key="type" :value="type">{{ type }}</option>
                  </select>
                </div>

                <!-- Length -->
                <div class="col-span-1">
                  <label class="block text-xs font-medium text-gray-700 mb-1">Length</label>
                  <input
                    v-model="column.length"
                    type="number"
                    class="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                    :disabled="!['varchar', 'char', 'int', 'bigint', 'decimal', 'float', 'double'].includes(column.type)"
                  >
                </div>

                <!-- Default -->
                <div class="col-span-2">
                  <label class="block text-xs font-medium text-gray-700 mb-1">Default</label>
                  <input
                    v-model="column.default"
                    type="text"
                    class="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                    placeholder="default value"
                  >
                </div>

                <!-- Options -->
                <div class="col-span-3">
                  <label class="block text-xs font-medium text-gray-700 mb-1">Options</label>
                  <div class="flex flex-wrap gap-2">
                    <label class="flex items-center text-xs">
                      <input v-model="column.nullable" type="checkbox" class="mr-1 text-xs">
                      <span>Null</span>
                    </label>
                    <label class="flex items-center text-xs">
                      <input v-model="column.primary" type="checkbox" class="mr-1 text-xs">
                      <span>Primary</span>
                    </label>
                    <label class="flex items-center text-xs">
                      <input v-model="column.autoIncrement" type="checkbox" class="mr-1 text-xs">
                      <span>Auto Inc</span>
                    </label>
                  </div>
                </div>

                <!-- Remove Button -->
                <div class="col-span-1">
                  <label class="block text-xs font-medium text-gray-700 mb-1">&nbsp;</label>
                  <button
                    @click="removeColumn(index)"
                    :disabled="columns.length === 1"
                    class="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white px-2 py-1 rounded text-xs"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
        <button
          @click="close"
          class="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
        >
          Cancel
        </button>
        <button
          @click="createTable"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Create Table
        </button>
      </div>
    </div>
  </div>
</template> 